import { 
  LocalHospital,
  Psychology,
  Healing,
  Biotech,
  MonitorHeart,
  MedicalServices,
  Person,
  AdminPanelSettings,
  RecordVoiceOver,
  SportsBasketball,
  Spa,
  AccessibilityNew,
  SelfImprovement,
  Favorite
} from '@mui/icons-material';

export interface ProfessionalPermissions {
  // Módulo de Pacientes
  patients_view: boolean;
  patients_create: boolean;
  patients_edit: boolean;
  patients_delete: boolean;
  
  // Módulo de Agendamentos
  appointments_view: boolean;
  appointments_create: boolean;
  appointments_edit: boolean;
  appointments_delete: boolean;
  appointments_manage_all: boolean; // Ver agendamentos de outros profissionais
  
  // Módulo de Prontuários
  ehr_view: boolean;
  ehr_create: boolean;
  ehr_edit: boolean;
  ehr_delete: boolean;
  ehr_view_all: boolean; // Ver prontuários de outros profissionais
  
  // Módulo de Telemedicina
  telemedicine_access: boolean;
  telemedicine_create_rooms: boolean;
  telemedicine_manage_all: boolean;
  
  // Módulo de Estoque/Farmácia
  inventory_view: boolean;
  inventory_create: boolean;
  inventory_edit: boolean;
  inventory_delete: boolean;
  
  // Módulo Financeiro
  financial_view: boolean;
  financial_create: boolean;
  financial_edit: boolean;
  financial_reports: boolean;
  
  // Módulo de Profissionais
  professionals_view: boolean;
  professionals_create: boolean;
  professionals_edit: boolean;
  professionals_delete: boolean;
  
  // Administração
  admin_access: boolean;
  admin_settings: boolean;
  admin_reports: boolean;
  admin_user_management: boolean;

  // Home Care (domicílio, rota, operação)
  homecare_field_access: boolean;
  homecare_manage_routes: boolean;
  homecare_view_all: boolean;
  homecare_admin_settings: boolean;
  homecare_analytics: boolean;
}

export interface ProfessionalTypeConfig {
  label: string;
  icon: any;
  registrationLabel: string;
  board: string;
  defaultPermissions: ProfessionalPermissions;
}

export type ProfessionalType = 'medico' | 'enfermeiro' | 'farmaceutico' | 'fisioterapeuta' | 'psicologo' | 'dentista' | 'nutricionista' | 'biomedico' | 'tecnico_enfermagem' | 'fonoaudiologo' | 'terapeuta_ocupacional' | 'educador_fisico' | 'acupunturista' | 'quiropraxista' | 'osteopata' | 'podologo' | 'massoterapeuta' | 'recepcionista' | 'administrativo';

export const PROFESSIONAL_TYPES: Record<ProfessionalType, ProfessionalTypeConfig> = {
  medico: {
    label: 'Médico',
    icon: LocalHospital,
    registrationLabel: 'CRM',
    board: 'Conselho Regional de Medicina',
    defaultPermissions: {
      patients_view: true, patients_create: true, patients_edit: true, patients_delete: false,
      appointments_view: true, appointments_create: true, appointments_edit: true, appointments_delete: false, appointments_manage_all: false,
      ehr_view: true, ehr_create: true, ehr_edit: true, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: true, telemedicine_create_rooms: true, telemedicine_manage_all: false,
      inventory_view: true, inventory_create: false, inventory_edit: false, inventory_delete: false,
      financial_view: false, financial_create: false, financial_edit: false, financial_reports: false,
      professionals_view: true, professionals_create: false, professionals_edit: false, professionals_delete: false,
      admin_access: false, admin_settings: false, admin_reports: false, admin_user_management: false,
      homecare_field_access: true, homecare_manage_routes: false, homecare_view_all: false, homecare_admin_settings: false, homecare_analytics: false
    }
  },
  enfermeiro: {
    label: 'Enfermeiro',
    icon: Healing,
    registrationLabel: 'COREN',
    board: 'Conselho Regional de Enfermagem',
    defaultPermissions: {
      patients_view: true, patients_create: true, patients_edit: true, patients_delete: false,
      appointments_view: true, appointments_create: true, appointments_edit: true, appointments_delete: false, appointments_manage_all: false,
      ehr_view: true, ehr_create: true, ehr_edit: true, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: true, telemedicine_create_rooms: false, telemedicine_manage_all: false,
      inventory_view: true, inventory_create: true, inventory_edit: true, inventory_delete: false,
      financial_view: false, financial_create: false, financial_edit: false, financial_reports: false,
      professionals_view: true, professionals_create: false, professionals_edit: false, professionals_delete: false,
      admin_access: false, admin_settings: false, admin_reports: false, admin_user_management: false,
      homecare_field_access: true, homecare_manage_routes: false, homecare_view_all: false, homecare_admin_settings: false, homecare_analytics: false
    }
  },
  farmaceutico: {
    label: 'Farmacêutico',
    icon: Biotech,
    registrationLabel: 'CRF',
    board: 'Conselho Regional de Farmácia',
    defaultPermissions: {
      patients_view: true, patients_create: false, patients_edit: false, patients_delete: false,
      appointments_view: true, appointments_create: false, appointments_edit: false, appointments_delete: false, appointments_manage_all: false,
      ehr_view: true, ehr_create: false, ehr_edit: false, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: false, telemedicine_create_rooms: false, telemedicine_manage_all: false,
      inventory_view: true, inventory_create: true, inventory_edit: true, inventory_delete: true,
      financial_view: false, financial_create: false, financial_edit: false, financial_reports: false,
      professionals_view: true, professionals_create: false, professionals_edit: false, professionals_delete: false,
      admin_access: false, admin_settings: false, admin_reports: false, admin_user_management: false,
      homecare_field_access: false, homecare_manage_routes: false, homecare_view_all: false, homecare_admin_settings: false, homecare_analytics: false
    }
  },
  fisioterapeuta: {
    label: 'Fisioterapeuta',
    icon: MonitorHeart,
    registrationLabel: 'CREFITO',
    board: 'Conselho Regional de Fisioterapia',
    defaultPermissions: {
      patients_view: true, patients_create: true, patients_edit: true, patients_delete: false,
      appointments_view: true, appointments_create: true, appointments_edit: true, appointments_delete: false, appointments_manage_all: false,
      ehr_view: true, ehr_create: true, ehr_edit: true, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: true, telemedicine_create_rooms: true, telemedicine_manage_all: false,
      inventory_view: true, inventory_create: false, inventory_edit: false, inventory_delete: false,
      financial_view: false, financial_create: false, financial_edit: false, financial_reports: false,
      professionals_view: true, professionals_create: false, professionals_edit: false, professionals_delete: false,
      admin_access: false, admin_settings: false, admin_reports: false, admin_user_management: false,
      homecare_field_access: true, homecare_manage_routes: false, homecare_view_all: false, homecare_admin_settings: false, homecare_analytics: false
    }
  },
  psicologo: {
    label: 'Psicólogo',
    icon: Psychology,
    registrationLabel: 'CRP',
    board: 'Conselho Regional de Psicologia',
    defaultPermissions: {
      patients_view: true, patients_create: true, patients_edit: true, patients_delete: false,
      appointments_view: true, appointments_create: true, appointments_edit: true, appointments_delete: false, appointments_manage_all: false,
      ehr_view: true, ehr_create: true, ehr_edit: true, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: true, telemedicine_create_rooms: true, telemedicine_manage_all: false,
      inventory_view: false, inventory_create: false, inventory_edit: false, inventory_delete: false,
      financial_view: false, financial_create: false, financial_edit: false, financial_reports: false,
      professionals_view: true, professionals_create: false, professionals_edit: false, professionals_delete: false,
      admin_access: false, admin_settings: false, admin_reports: false, admin_user_management: false,
      homecare_field_access: false, homecare_manage_routes: false, homecare_view_all: false, homecare_admin_settings: false, homecare_analytics: false
    }
  },
  dentista: {
    label: 'Dentista',
    icon: MedicalServices,
    registrationLabel: 'CRO',
    board: 'Conselho Regional de Odontologia',
    defaultPermissions: {
      patients_view: true, patients_create: true, patients_edit: true, patients_delete: false,
      appointments_view: true, appointments_create: true, appointments_edit: true, appointments_delete: false, appointments_manage_all: false,
      ehr_view: true, ehr_create: true, ehr_edit: true, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: true, telemedicine_create_rooms: true, telemedicine_manage_all: false,
      inventory_view: true, inventory_create: false, inventory_edit: false, inventory_delete: false,
      financial_view: false, financial_create: false, financial_edit: false, financial_reports: false,
      professionals_view: true, professionals_create: false, professionals_edit: false, professionals_delete: false,
      admin_access: false, admin_settings: false, admin_reports: false, admin_user_management: false,
      homecare_field_access: false, homecare_manage_routes: false, homecare_view_all: false, homecare_admin_settings: false, homecare_analytics: false
    }
  },
  nutricionista: {
    label: 'Nutricionista',
    icon: Healing,
    registrationLabel: 'CRN',
    board: 'Conselho Regional de Nutricionistas',
    defaultPermissions: {
      patients_view: true, patients_create: true, patients_edit: true, patients_delete: false,
      appointments_view: true, appointments_create: true, appointments_edit: true, appointments_delete: false, appointments_manage_all: false,
      ehr_view: true, ehr_create: true, ehr_edit: true, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: true, telemedicine_create_rooms: true, telemedicine_manage_all: false,
      inventory_view: false, inventory_create: false, inventory_edit: false, inventory_delete: false,
      financial_view: false, financial_create: false, financial_edit: false, financial_reports: false,
      professionals_view: true, professionals_create: false, professionals_edit: false, professionals_delete: false,
      admin_access: false, admin_settings: false, admin_reports: false, admin_user_management: false,
      homecare_field_access: false, homecare_manage_routes: false, homecare_view_all: false, homecare_admin_settings: false, homecare_analytics: false
    }
  },
  biomedico: {
    label: 'Biomédico',
    icon: Biotech,
    registrationLabel: 'CRBM',
    board: 'Conselho Regional de Biomedicina',
    defaultPermissions: {
      patients_view: true, patients_create: false, patients_edit: false, patients_delete: false,
      appointments_view: true, appointments_create: false, appointments_edit: false, appointments_delete: false, appointments_manage_all: false,
      ehr_view: true, ehr_create: true, ehr_edit: true, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: false, telemedicine_create_rooms: false, telemedicine_manage_all: false,
      inventory_view: true, inventory_create: true, inventory_edit: true, inventory_delete: false,
      financial_view: false, financial_create: false, financial_edit: false, financial_reports: false,
      professionals_view: true, professionals_create: false, professionals_edit: false, professionals_delete: false,
      admin_access: false, admin_settings: false, admin_reports: false, admin_user_management: false,
      homecare_field_access: false, homecare_manage_routes: false, homecare_view_all: false, homecare_admin_settings: false, homecare_analytics: false
    }
  },
  tecnico_enfermagem: {
    label: 'Técnico em Enfermagem',
    icon: Healing,
    registrationLabel: 'COREN',
    board: 'Conselho Regional de Enfermagem',
    defaultPermissions: {
      patients_view: true, patients_create: false, patients_edit: true, patients_delete: false,
      appointments_view: true, appointments_create: false, appointments_edit: false, appointments_delete: false, appointments_manage_all: false,
      ehr_view: true, ehr_create: true, ehr_edit: true, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: false, telemedicine_create_rooms: false, telemedicine_manage_all: false,
      inventory_view: true, inventory_create: false, inventory_edit: true, inventory_delete: false,
      financial_view: false, financial_create: false, financial_edit: false, financial_reports: false,
      professionals_view: true, professionals_create: false, professionals_edit: false, professionals_delete: false,
      admin_access: false, admin_settings: false, admin_reports: false, admin_user_management: false,
      homecare_field_access: true, homecare_manage_routes: false, homecare_view_all: false, homecare_admin_settings: false, homecare_analytics: false
    }
  },
  fonoaudiologo: {
    label: 'Fonoaudiólogo',
    icon: RecordVoiceOver,
    registrationLabel: 'CRFa',
    board: 'Conselho Regional de Fonoaudiologia',
    defaultPermissions: {
      patients_view: true, patients_create: true, patients_edit: true, patients_delete: false,
      appointments_view: true, appointments_create: true, appointments_edit: true, appointments_delete: false, appointments_manage_all: false,
      ehr_view: true, ehr_create: true, ehr_edit: true, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: true, telemedicine_create_rooms: true, telemedicine_manage_all: false,
      inventory_view: false, inventory_create: false, inventory_edit: false, inventory_delete: false,
      financial_view: false, financial_create: false, financial_edit: false, financial_reports: false,
      professionals_view: true, professionals_create: false, professionals_edit: false, professionals_delete: false,
      admin_access: false, admin_settings: false, admin_reports: false, admin_user_management: false,
      homecare_field_access: false, homecare_manage_routes: false, homecare_view_all: false, homecare_admin_settings: false, homecare_analytics: false
    }
  },
  terapeuta_ocupacional: {
    label: 'Terapeuta Ocupacional',
    icon: AccessibilityNew,
    registrationLabel: 'CREFITO',
    board: 'Conselho Regional de Fisioterapia e Terapia Ocupacional',
    defaultPermissions: {
      patients_view: true, patients_create: true, patients_edit: true, patients_delete: false,
      appointments_view: true, appointments_create: true, appointments_edit: true, appointments_delete: false, appointments_manage_all: false,
      ehr_view: true, ehr_create: true, ehr_edit: true, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: true, telemedicine_create_rooms: true, telemedicine_manage_all: false,
      inventory_view: false, inventory_create: false, inventory_edit: false, inventory_delete: false,
      financial_view: false, financial_create: false, financial_edit: false, financial_reports: false,
      professionals_view: true, professionals_create: false, professionals_edit: false, professionals_delete: false,
      admin_access: false, admin_settings: false, admin_reports: false, admin_user_management: false,
      homecare_field_access: false, homecare_manage_routes: false, homecare_view_all: false, homecare_admin_settings: false, homecare_analytics: false
    }
  },
  educador_fisico: {
    label: 'Educador Físico',
    icon: SportsBasketball,
    registrationLabel: 'CREF',
    board: 'Conselho Regional de Educação Física',
    defaultPermissions: {
      patients_view: true, patients_create: true, patients_edit: true, patients_delete: false,
      appointments_view: true, appointments_create: true, appointments_edit: true, appointments_delete: false, appointments_manage_all: false,
      ehr_view: true, ehr_create: true, ehr_edit: true, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: true, telemedicine_create_rooms: true, telemedicine_manage_all: false,
      inventory_view: false, inventory_create: false, inventory_edit: false, inventory_delete: false,
      financial_view: false, financial_create: false, financial_edit: false, financial_reports: false,
      professionals_view: true, professionals_create: false, professionals_edit: false, professionals_delete: false,
      admin_access: false, admin_settings: false, admin_reports: false, admin_user_management: false,
      homecare_field_access: false, homecare_manage_routes: false, homecare_view_all: false, homecare_admin_settings: false, homecare_analytics: false
    }
  },
  acupunturista: {
    label: 'Acupunturista',
    icon: SelfImprovement,
    registrationLabel: 'Registro',
    board: 'Conselho Regional de Medicina (Médicos) / Registro Profissional',
    defaultPermissions: {
      patients_view: true, patients_create: true, patients_edit: true, patients_delete: false,
      appointments_view: true, appointments_create: true, appointments_edit: true, appointments_delete: false, appointments_manage_all: false,
      ehr_view: true, ehr_create: true, ehr_edit: true, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: true, telemedicine_create_rooms: true, telemedicine_manage_all: false,
      inventory_view: false, inventory_create: false, inventory_edit: false, inventory_delete: false,
      financial_view: false, financial_create: false, financial_edit: false, financial_reports: false,
      professionals_view: true, professionals_create: false, professionals_edit: false, professionals_delete: false,
      admin_access: false, admin_settings: false, admin_reports: false, admin_user_management: false,
      homecare_field_access: false, homecare_manage_routes: false, homecare_view_all: false, homecare_admin_settings: false, homecare_analytics: false
    }
  },
  quiropraxista: {
    label: 'Quiropraxista',
    icon: Healing,
    registrationLabel: 'WFC',
    board: 'World Federation of Chiropractic',
    defaultPermissions: {
      patients_view: true, patients_create: true, patients_edit: true, patients_delete: false,
      appointments_view: true, appointments_create: true, appointments_edit: true, appointments_delete: false, appointments_manage_all: false,
      ehr_view: true, ehr_create: true, ehr_edit: true, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: false, telemedicine_create_rooms: false, telemedicine_manage_all: false,
      inventory_view: false, inventory_create: false, inventory_edit: false, inventory_delete: false,
      financial_view: false, financial_create: false, financial_edit: false, financial_reports: false,
      professionals_view: true, professionals_create: false, professionals_edit: false, professionals_delete: false,
      admin_access: false, admin_settings: false, admin_reports: false, admin_user_management: false,
      homecare_field_access: false, homecare_manage_routes: false, homecare_view_all: false, homecare_admin_settings: false, homecare_analytics: false
    }
  },
  osteopata: {
    label: 'Osteopata',
    icon: Spa,
    registrationLabel: 'Registro',
    board: 'Registro Profissional',
    defaultPermissions: {
      patients_view: true, patients_create: true, patients_edit: true, patients_delete: false,
      appointments_view: true, appointments_create: true, appointments_edit: true, appointments_delete: false, appointments_manage_all: false,
      ehr_view: true, ehr_create: true, ehr_edit: true, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: false, telemedicine_create_rooms: false, telemedicine_manage_all: false,
      inventory_view: false, inventory_create: false, inventory_edit: false, inventory_delete: false,
      financial_view: false, financial_create: false, financial_edit: false, financial_reports: false,
      professionals_view: true, professionals_create: false, professionals_edit: false, professionals_delete: false,
      admin_access: false, admin_settings: false, admin_reports: false, admin_user_management: false,
      homecare_field_access: false, homecare_manage_routes: false, homecare_view_all: false, homecare_admin_settings: false, homecare_analytics: false
    }
  },
  podologo: {
    label: 'Podólogo',
    icon: Favorite,
    registrationLabel: 'Registro',
    board: 'Registro Profissional',
    defaultPermissions: {
      patients_view: true, patients_create: true, patients_edit: true, patients_delete: false,
      appointments_view: true, appointments_create: true, appointments_edit: true, appointments_delete: false, appointments_manage_all: false,
      ehr_view: true, ehr_create: true, ehr_edit: true, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: false, telemedicine_create_rooms: false, telemedicine_manage_all: false,
      inventory_view: false, inventory_create: false, inventory_edit: false, inventory_delete: false,
      financial_view: false, financial_create: false, financial_edit: false, financial_reports: false,
      professionals_view: true, professionals_create: false, professionals_edit: false, professionals_delete: false,
      admin_access: false, admin_settings: false, admin_reports: false, admin_user_management: false,
      homecare_field_access: false, homecare_manage_routes: false, homecare_view_all: false, homecare_admin_settings: false, homecare_analytics: false
    }
  },
  massoterapeuta: {
    label: 'Massoterapeuta',
    icon: Spa,
    registrationLabel: 'Registro',
    board: 'Registro Profissional',
    defaultPermissions: {
      patients_view: true, patients_create: true, patients_edit: true, patients_delete: false,
      appointments_view: true, appointments_create: true, appointments_edit: true, appointments_delete: false, appointments_manage_all: false,
      ehr_view: false, ehr_create: false, ehr_edit: false, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: false, telemedicine_create_rooms: false, telemedicine_manage_all: false,
      inventory_view: false, inventory_create: false, inventory_edit: false, inventory_delete: false,
      financial_view: false, financial_create: false, financial_edit: false, financial_reports: false,
      professionals_view: true, professionals_create: false, professionals_edit: false, professionals_delete: false,
      admin_access: false, admin_settings: false, admin_reports: false, admin_user_management: false,
      homecare_field_access: false, homecare_manage_routes: false, homecare_view_all: false, homecare_admin_settings: false, homecare_analytics: false
    }
  },
  recepcionista: {
    label: 'Recepcionista',
    icon: Person,
    registrationLabel: 'Registro',
    board: 'Registro Interno',
    defaultPermissions: {
      patients_view: true, patients_create: true, patients_edit: true, patients_delete: false,
      appointments_view: true, appointments_create: true, appointments_edit: true, appointments_delete: false, appointments_manage_all: true,
      ehr_view: false, ehr_create: false, ehr_edit: false, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: false, telemedicine_create_rooms: false, telemedicine_manage_all: false,
      inventory_view: false, inventory_create: false, inventory_edit: false, inventory_delete: false,
      financial_view: false, financial_create: false, financial_edit: false, financial_reports: false,
      professionals_view: true, professionals_create: false, professionals_edit: false, professionals_delete: false,
      admin_access: false, admin_settings: false, admin_reports: false, admin_user_management: false,
      homecare_field_access: false, homecare_manage_routes: false, homecare_view_all: true, homecare_admin_settings: false, homecare_analytics: false
    }
  },
  administrativo: {
    label: 'Administrativo',
    icon: AdminPanelSettings,
    registrationLabel: 'Registro',
    board: 'Registro Interno',
    defaultPermissions: {
      patients_view: true, patients_create: false, patients_edit: false, patients_delete: false,
      appointments_view: true, appointments_create: false, appointments_edit: false, appointments_delete: false, appointments_manage_all: true,
      ehr_view: false, ehr_create: false, ehr_edit: false, ehr_delete: false, ehr_view_all: false,
      telemedicine_access: false, telemedicine_create_rooms: false, telemedicine_manage_all: false,
      inventory_view: true, inventory_create: false, inventory_edit: false, inventory_delete: false,
      financial_view: true, financial_create: true, financial_edit: true, financial_reports: true,
      professionals_view: true, professionals_create: true, professionals_edit: true, professionals_delete: false,
      admin_access: true, admin_settings: true, admin_reports: true, admin_user_management: false,
      homecare_field_access: true, homecare_manage_routes: true, homecare_view_all: true, homecare_admin_settings: true, homecare_analytics: true
    }
  }
};

// Especialidades por tipo de profissional
export const SPECIALTIES_BY_TYPE: Record<ProfessionalType, string[]> = {
  medico: [
    'Cardiologia', 'Dermatologia', 'Endocrinologia', 'Gastroenterologia',
    'Ginecologia', 'Neurologia', 'Oftalmologia', 'Ortopedia', 'Otorrinolaringologia',
    'Pediatria', 'Pneumologia', 'Psiquiatria', 'Radiologia', 'Urologia',
    'Anestesiologia', 'Cirurgia Geral', 'Clínica Médica', 'Medicina do Trabalho',
    'Medicina Preventiva', 'Patologia', 'Reumatologia', 'Medicina Nuclear'
  ],
  enfermeiro: [
    'Enfermagem Geral', 'UTI', 'Emergência', 'Pediatria', 'Obstétrica',
    'Saúde Mental', 'Home Care', 'Instrumentação Cirúrgica'
  ],
  farmaceutico: [
    'Farmácia Clínica', 'Farmacologia', 'Análises Clínicas',
    'Farmácia Hospitalar', 'Toxicologia'
  ],
  fisioterapeuta: [
    'Fisioterapia Geral', 'Ortopédica', 'Neurológica', 'Respiratória',
    'Dermatofuncional', 'Esportiva', 'Aquática', 'Pediátrica'
  ],
  psicologo: [
    'Psicologia Clínica', 'Terapia Cognitivo-Comportamental', 'Psicanálise',
    'Psicologia Infantil', 'Neuropsicologia', 'Psicologia Hospitalar'
  ],
  dentista: [
    'Odontologia Geral', 'Ortodontia', 'Endodontia', 'Periodontia',
    'Implantodontia', 'Cirurgia Oral', 'Odontopediatria', 'Prótese'
  ],
  nutricionista: [
    'Nutrição Clínica', 'Nutrição Esportiva', 'Nutrição Materno-Infantil',
    'Nutrição Geriátrica', 'Nutrição Hospitalar'
  ],
  biomedico: [
    'Análises Clínicas', 'Microbiologia', 'Hematologia',
    'Bioquímica', 'Imunologia', 'Patologia'
  ],
  tecnico_enfermagem: [
    'Técnico em Enfermagem Geral', 'UTI', 'Emergência', 'Pediatria'
  ],
  fonoaudiologo: [
    'Fonoaudiologia Clínica', 'Fonoaudiologia Infantil', 'Fonoaudiologia Geriátrica',
    'Fonoaudiologia Hospitalar', 'Fonoaudiologia Clínica Pediátrica'
  ],
  terapeuta_ocupacional: [
    'Terapia Ocupacional Geral', 'Terapia Ocupacional Pediátrica', 'Terapia Ocupacional Geriátrica',
    'Terapia Ocupacional Hospitalar', 'Terapia Ocupacional Clínica'
  ],
  educador_fisico: [
    'Educação Física Geral', 'Educação Física Infantil', 'Educação Física Adulta',
    'Educação Física Idosa', 'Educação Física Adaptativa'
  ],
  acupunturista: [
    'Acupuntura Clínica', 'Acupuntura Hospitalar', 'Acupuntura Pediátrica',
    'Acupuntura Geriátrica', 'Acupuntura Clínica Pediátrica'
  ],
  quiropraxista: [
    'Quiropraxia Clínica', 'Quiropraxia Hospitalar', 'Quiropraxia Pediátrica',
    'Quiropraxia Geriátrica', 'Quiropraxia Clínica Pediátrica'
  ],
  osteopata: [
    'Osteopatia Clínica', 'Osteopatia Hospitalar', 'Osteopatia Pediátrica',
    'Osteopatia Geriátrica', 'Osteopatia Clínica Pediátrica'
  ],
  podologo: [
    'Podologia Clínica', 'Podologia Hospitalar', 'Podologia Pediátrica',
    'Podologia Geriátrica', 'Podologia Clínica Pediátrica'
  ],
  massoterapeuta: [
    'Massoterapia Clínica', 'Massoterapia Hospitalar', 'Massoterapia Pediátrica',
    'Massoterapia Geriátrica', 'Massoterapia Clínica Pediátrica'
  ],
  recepcionista: [
    'Atendimento Geral', 'Agendamento', 'Telefonia'
  ],
  administrativo: [
    'Administrativo Geral', 'Financeiro', 'Recursos Humanos', 'Compras'
  ]
};

// Descrições das permissões para interface
export const PERMISSION_DESCRIPTIONS = {
  patients_view: 'Visualizar pacientes',
  patients_create: 'Cadastrar novos pacientes',
  patients_edit: 'Editar dados de pacientes',
  patients_delete: 'Excluir pacientes',
  
  appointments_view: 'Visualizar agendamentos',
  appointments_create: 'Criar novos agendamentos',
  appointments_edit: 'Editar agendamentos',
  appointments_delete: 'Excluir agendamentos',
  appointments_manage_all: 'Gerenciar agendamentos de todos os profissionais',
  
  ehr_view: 'Visualizar prontuários',
  ehr_create: 'Criar registros em prontuários',
  ehr_edit: 'Editar prontuários',
  ehr_delete: 'Excluir registros de prontuários',
  ehr_view_all: 'Visualizar prontuários de todos os profissionais',
  
  telemedicine_access: 'Acessar módulo de telemedicina',
  telemedicine_create_rooms: 'Criar salas de videochamada',
  telemedicine_manage_all: 'Gerenciar todas as sessões de telemedicina',
  
  inventory_view: 'Visualizar estoque',
  inventory_create: 'Adicionar itens ao estoque',
  inventory_edit: 'Editar itens do estoque',
  inventory_delete: 'Remover itens do estoque',
  
  financial_view: 'Visualizar dados financeiros',
  financial_create: 'Criar registros financeiros',
  financial_edit: 'Editar registros financeiros',
  financial_reports: 'Gerar relatórios financeiros',
  
  professionals_view: 'Visualizar profissionais',
  professionals_create: 'Cadastrar novos profissionais',
  professionals_edit: 'Editar dados de profissionais',
  professionals_delete: 'Excluir profissionais',
  
  admin_access: 'Acesso administrativo',
  admin_settings: 'Configurações do sistema',
  admin_reports: 'Relatórios administrativos',
  admin_user_management: 'Gerenciar usuários do sistema',
  homecare_field_access: 'App de campo: rota do dia e atendimentos domiciliares',
  homecare_manage_routes: 'Montar e ajustar rotas de todos os profissionais',
  homecare_view_all: 'Ver rotas e visitas de todos (coordenação)',
  homecare_admin_settings: 'Configurar deslocamento, janelas e veículos (Home Care)',
  homecare_analytics: 'Relatórios e analytics de Home Care (lucro, km, região)'
}; 